import type { BookingStatusResponse } from "@ascleo/frontdesk-contracts/scheduling/recovery";

/** A prior result may be presented only to the currently identified recorded patient.
 * @since 1.1.8
 * @example const presentation: BookingRecoveryPresentation = { kind: "identification-required" };
 */
export type BookingRecoveryPresentation =
  | { readonly kind: "identification-required" }
  | { readonly kind: "patient-mismatch" }
  | { readonly kind: "unknown" | "expired" }
  | { readonly kind: "recorded-outcome"; readonly response: BookingStatusResponse };

/** Gate receipt-authenticated status against the supported scheduler identification.
 * This does not create a server identity proof or use patient-info eGK authority.
 * Mismatch and missing identity results contain no previous appointment metadata.
 * @since 1.1.8
 * @example bookingRecoveryPresentation({ status: "expired" }, undefined);
 */
export function bookingRecoveryPresentation(
  response: BookingStatusResponse,
  currentPatientId: string | undefined,
): BookingRecoveryPresentation {
  if (response.status === "unknown" || response.status === "expired") return { kind: response.status };
  if (currentPatientId === undefined || currentPatientId.length === 0) return { kind: "identification-required" };
  if (response.binding.patientId !== currentPatientId) return { kind: "patient-mismatch" };
  return { kind: "recorded-outcome", response };
}
